import { useCallback, useEffect, useRef, useState } from "react";
import { setDraftBuffer, getDraftBuffer, clearDraftBuffer } from "@/lib/secureVault";

/**
 * useAutoSavedDraft
 *
 * Persists in-progress composer state to the encrypted vault under a stable
 * `surface` key (e.g. "journal", "letters", "drafts"). Debounced so we don't
 * thrash the vault on every keystroke. Restores on mount.
 *
 * Usage:
 *   const { value, setValue, ready, lastSavedAt, clear } =
 *     useAutoSavedDraft("journal", { text: "", prompt: null });
 *
 * The shape of `value` is whatever you pass in as initial state. We just
 * persist the whole object.
 */
export function useAutoSavedDraft(surface, initial, { debounceMs = 600 } = {}) {
  const [value, setValueState] = useState(initial);
  const [ready, setReady] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const timer = useRef(null);

  // Restore on mount.
  useEffect(() => {
    let cancelled = false;
    getDraftBuffer(surface).then((restored) => {
      if (cancelled) return;
      if (restored && typeof restored === "object") {
        setValueState({ ...initial, ...restored });
      }
      setReady(true);
    });
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface]);

  // Wrapped setter that debounces a write.
  const setValue = useCallback(
    (next) => {
      const resolved = typeof next === "function" ? next(value) : next;
      setValueState(resolved);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        // Only persist if there's something to save (avoid writing empty buffers).
        const hasContent = Object.values(resolved || {}).some(
          (v) => typeof v === "string" && v.trim().length > 0,
        );
        if (hasContent) {
          setDraftBuffer(surface, resolved).then(() => setLastSavedAt(Date.now()));
        }
      }, debounceMs);
    },
    [surface, debounceMs, value],
  );

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setValueState(initial);
    setLastSavedAt(null);
    clearDraftBuffer(surface);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface, initial]);

  return { value, setValue, ready, lastSavedAt, clear };
}
